import { Helmet } from "react-helmet-async";
import React, { useEffect, useMemo, useState } from "react";
import { MapPin, Calendar, Users, Euro, FileText, X } from "lucide-react";
import { listRitiri, createPrenotazioneRitiro, adminListPrenotazioniRitiri, adminUpdatePrenotazioneRitiroStatus } from "../lib/db";
import { useAuth } from "../context/AuthContext";
import { formatSupabaseError } from "../lib/supabase";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

function fmtDate(d) {
  return new Date(d + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "long" });
}

function isPastRitiro(r) {
  const todayKey = new Date().toISOString().slice(0, 10);
  return (r.end_date || r.start_date) < todayKey;
}

const STATUS_STYLE = {
  pending: "bg-yellow-400/15 text-yellow-300 border-yellow-400/30",
  confirmed: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  cancelled: "bg-red-400/15 text-red-300 border-red-400/30",
};
const STATUS_LABEL = { pending: "In attesa", confirmed: "Confermata", cancelled: "Annullata" };

export default function RitiriEstivi() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [ritiri, setRitiri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingFor, setBookingFor] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [prenotazioni, setPrenotazioni] = useState([]);
  const [expandedRitiro, setExpandedRitiro] = useState(null);

  useEffect(() => {
    listRitiri().then(setRitiri).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    adminListPrenotazioniRitiri().then(setPrenotazioni).catch(() => {});
  }, [isAdmin]);

  const prenotazioniByRitiro = useMemo(() => {
    const map = {};
    prenotazioni.forEach((p) => {
      if (!map[p.ritiro_id]) map[p.ritiro_id] = [];
      map[p.ritiro_id].push(p);
    });
    return map;
  }, [prenotazioni]);

  const submitBooking = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const f = new FormData(e.currentTarget);
    try {
      await createPrenotazioneRitiro(bookingFor.id, {
        full_name: f.get("full_name"),
        email: f.get("email"),
        phone: f.get("phone"),
        ragazzo_nome: f.get("ragazzo_nome"),
        num_partecipanti: parseInt(f.get("num_partecipanti"), 10) || 1,
        notes: f.get("notes"),
      });
      toast.success("Richiesta inviata!", { description: "Ti contatteremo per confermare la prenotazione." });
      setBookingFor(null);
    } catch (err) {
      toast.error(formatSupabaseError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await adminUpdatePrenotazioneRitiroStatus(id, status);
      setPrenotazioni((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
    } catch (err) {
      toast.error(formatSupabaseError(err));
    }
  };

  return (
    <div data-testid="ritiri-estivi-page">
      <Helmet>
        <title>Ritiri Estivi – ASD Rubiana Calcio</title>
        <meta name="description" content="I ritiri estivi dell'ASD Rubiana Calcio per squadre giovanili e non: date, luoghi e modulo di prenotazione." />
        <meta property="og:title" content="Ritiri Estivi – ASD Rubiana Calcio" />
        <meta property="og:description" content="Ritiri estivi per squadre giovanili e non dell'ASD Rubiana Calcio." />
        <meta property="og:url" content="https://rubianacalcio.it/ritiri-estivi" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://rubianacalcio.it/images/ritiri-estivi-locandina.jpg" />
      </Helmet>

      {/* Hero: locandina */}
      <section className="border-b border-zinc-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <img
            src="/images/ritiri-estivi-locandina.jpg"
            alt="Ritiri Estivi ASD Rubiana Calcio – al nostro campo"
            className="w-full rounded-2xl border border-zinc-800 shadow-2xl"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
            <p className="max-w-3xl mx-auto text-center">

  <span className="block text-2xl font-bold text-yellow-400 mb-4">
    Ritiri estivi al Rubiana Calcio
  </span>

  <span className="block text-lg md:text-xl text-white font-medium mb-4">
    Un’estate di calcio, amicizia e divertimento... proprio a casa nostra! 💙💛
  </span>

  <span className="block text-base md:text-lg text-zinc-300 leading-relaxed mb-6">
    Il campo del Rubiana Calcio durante l’estate si trasforma nel luogo ideale
    dove vivere il calcio a 360°: allenarsi, giocare, stare insieme e soprattutto
    divertirsi.
  </span>

  <span className="block text-base md:text-lg text-zinc-300 leading-relaxed mb-4">
    I nostri ritiri estivi sono pensati per permettere ai ragazzi di continuare
    a vivere la loro passione anche durante la pausa estiva, in un ambiente
    familiare, accogliente e interamente dedicato a loro.
  </span>

  <span className="block text-base md:text-lg text-white leading-loose mb-6">
    ⚽ Allenamenti e attività sul campo<br />
    🤝 Amicizia e spirito di squadra<br />
    🎯 Giochi e attività divertenti<br />
    💪 Crescita sportiva e personale
  </span>

  <span className="block text-lg md:text-xl font-semibold text-yellow-400 mb-5">
    💙💛 Tutto questo... al Rubiana Calcio!
  </span>

  <span className="block text-base md:text-lg text-zinc-300 leading-relaxed mb-3">
    Perché un ritiro non è soltanto allenarsi.
  </span>

  <span className="block text-base md:text-lg text-white font-medium leading-relaxed mb-5">
    È correre insieme, ridere insieme, imparare insieme e portarsi a casa
    ricordi che durano nel tempo.
  </span>

  <span className="block text-xl md:text-2xl font-bold text-yellow-400">
    Vieni a vivere l’estate con noi!
  </span>

  <span className="block text-lg md:text-xl text-white font-semibold mt-3">
    Rubiana Calcio — più di una squadra, una famiglia. ⚽💙💛
  </span>

</p>
        </div>
      </section>

      {/* Elenco ritiri */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {loading ? (
          <div className="grid md:grid-cols-2 gap-8">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] rounded-2xl bg-zinc-900 animate-pulse" />
            ))}
          </div>
        ) : ritiri.length === 0 ? (
          <p className="text-zinc-500 text-center py-12">Nessun ritiro estivo in programma al momento. Torna a trovarci presto!</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {ritiri.map((r) => {
              const past = isPastRitiro(r);
              return (
              <article
                key={r.id}
                className={`rounded-2xl overflow-hidden bg-zinc-900 border transition-colors ${
                  past ? "border-zinc-800 opacity-60" : "border-zinc-800 hover:border-yellow-400/40"
                }`}
                data-testid={`ritiro-${r.id}`}
              >
                {r.photos?.length > 0 && (
                  <div className={`aspect-[16/9] overflow-hidden ${past ? "grayscale" : ""}`}>
                    <img src={r.photos[0]} alt={r.title} className="h-full w-full object-cover" />
                  </div>
                )}
                <div className="p-6 space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {r.team && <div className="text-overline text-yellow-400">{r.team}</div>}
                      {past && (
                        <span className="text-[10px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-zinc-800 text-zinc-400 border-zinc-700">
                          Concluso
                        </span>
                      )}
                    </div>
                    <h3 className="font-display font-bold text-2xl uppercase tracking-tight">{r.title}</h3>
                  </div>
                  {r.description && <p className="text-sm text-zinc-400 leading-relaxed">{r.description}</p>}
                  <div className="flex flex-wrap gap-4 text-sm text-zinc-300">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-4 w-4 text-yellow-400" />
                      {fmtDate(r.start_date)}{r.end_date ? ` – ${fmtDate(r.end_date)}` : ""}
                    </div>
                    {r.location && (
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-4 w-4 text-yellow-400" />
                        {r.location}
                      </div>
                    )}
                    {r.price != null && (
                      <div className="flex items-center gap-1.5">
                        <Euro className="h-4 w-4 text-yellow-400" />
                        {r.price}
                      </div>
                    )}
                    {r.max_partecipanti != null && (
                      <div className="flex items-center gap-1.5">
                        <Users className="h-4 w-4 text-yellow-400" />
                        {r.max_partecipanti} posti
                      </div>
                    )}
                  </div>

                  {r.photos?.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {r.photos.slice(1, 5).map((url, idx) => (
                        <img key={idx} src={url} alt="" className="aspect-square object-cover rounded-lg" />
                      ))}
                    </div>
                  )}

                  {r.documents?.length > 0 && (
                    <div className="space-y-1.5">
                      {r.documents.map((doc, idx) => (
                        <a
                          key={idx}
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 text-sm text-zinc-300 hover:text-yellow-400 transition-colors"
                        >
                          <FileText className="h-4 w-4 shrink-0" /> {doc.label}
                        </a>
                      ))}
                    </div>
                  )}

                  <Button
                    onClick={() => setBookingFor(r)}
                    disabled={past}
                    className={`w-full font-display font-bold rounded-full ${
                      past ? "bg-zinc-800 text-zinc-500 cursor-not-allowed hover:bg-zinc-800" : "bg-yellow-400 text-zinc-950 hover:bg-yellow-300"
                    }`}
                    data-testid={`prenota-${r.id}`}
                  >
                    {past ? "Ritiro concluso" : "Prenota il tuo posto"}
                  </Button>

                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => setExpandedRitiro(expandedRitiro === r.id ? null : r.id)}
                      className="w-full text-xs font-display font-bold uppercase tracking-wider text-zinc-500 hover:text-yellow-400 transition-colors pt-1"
                    >
                      {expandedRitiro === r.id ? "Nascondi prenotazioni" : `Vedi prenotazioni (${(prenotazioniByRitiro[r.id] || []).length})`}
                    </button>
                  )}

                  {isAdmin && expandedRitiro === r.id && (
                    <div className="rounded-xl border border-zinc-800 bg-zinc-950 divide-y divide-zinc-800">
                      {(prenotazioniByRitiro[r.id] || []).length === 0 ? (
                        <p className="text-xs text-zinc-500 px-4 py-4">Nessuna prenotazione per questo ritiro.</p>
                      ) : (
                        prenotazioniByRitiro[r.id].map((p) => (
                          <div key={p.id} className="px-4 py-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{p.full_name}{p.ragazzo_nome ? ` (${p.ragazzo_nome})` : ""}</div>
                              <div className="text-xs text-zinc-500 truncate">{p.email} • {p.phone} • {p.num_partecipanti} pers.</div>
                            </div>
                            <Select value={p.status} onValueChange={(v) => updateStatus(p.id, v)}>
                              <SelectTrigger className={`w-[110px] h-7 text-xs border ${STATUS_STYLE[p.status]}`}>
                                <SelectValue>{STATUS_LABEL[p.status]}</SelectValue>
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                                <SelectItem value="pending">In attesa</SelectItem>
                                <SelectItem value="confirmed">Confermata</SelectItem>
                                <SelectItem value="cancelled">Annullata</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </article>
              );
            })}
          </div>
        )}
      </section>

      {/* Calendario riassuntivo */}
      {ritiri.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-zinc-900">
          <div className="text-overline text-yellow-400 mb-4">// Agenda ritiri</div>
          <h2 className="font-display font-bold text-3xl uppercase tracking-tight mb-8">Tutte le date</h2>
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 divide-y divide-zinc-800 overflow-hidden">
            {ritiri.map((r) => {
              const past = isPastRitiro(r);
              return (
              <div key={r.id} className={`px-5 py-4 flex items-center gap-4 ${past ? "opacity-50" : ""}`}>
                <div className="h-12 w-12 rounded-lg bg-zinc-950 border border-zinc-800 flex flex-col items-center justify-center shrink-0">
                  <span className="text-[9px] text-zinc-500 uppercase leading-none">
                    {new Date(r.start_date + "T00:00:00").toLocaleDateString("it-IT", { month: "short" })}
                  </span>
                  <span className="text-base font-black leading-none mt-0.5">{new Date(r.start_date + "T00:00:00").getDate()}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-sm truncate">
                    {r.title}{r.team ? ` — ${r.team}` : ""}
                    {past && <span className="ml-2 text-[10px] font-normal uppercase tracking-wider text-zinc-500">(concluso)</span>}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {fmtDate(r.start_date)}{r.end_date ? ` – ${fmtDate(r.end_date)}` : ""}
                    {r.location ? ` • ${r.location}` : ""}
                  </div>
                </div>
                {isAdmin && (
                  <span className="text-xs font-display font-bold text-zinc-500 shrink-0">
                    {(prenotazioniByRitiro[r.id] || []).length} prenotazioni
                  </span>
                )}
              </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Dialog prenotazione */}
      <Dialog open={!!bookingFor} onOpenChange={(open) => { if (!open) setBookingFor(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Prenota: {bookingFor?.title}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Compila il modulo, ti contatteremo per confermare disponibilità e modalità di pagamento.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBooking} className="space-y-3">
            <div>
              <Label className="text-zinc-300">Nome e cognome (genitore/richiedente)</Label>
              <Input name="full_name" required className="bg-zinc-950 border-zinc-800 mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300">Email</Label>
                <Input type="email" name="email" required className="bg-zinc-950 border-zinc-800 mt-1.5" />
              </div>
              <div>
                <Label className="text-zinc-300">Telefono</Label>
                <Input name="phone" required className="bg-zinc-950 border-zinc-800 mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300">Nome del ragazzo/a <span className="text-zinc-600">(opzionale)</span></Label>
              <Input name="ragazzo_nome" className="bg-zinc-950 border-zinc-800 mt-1.5" />
            </div>
            <div>
              <Label className="text-zinc-300">Numero partecipanti</Label>
              <Input type="number" name="num_partecipanti" min={1} defaultValue={1} className="bg-zinc-950 border-zinc-800 mt-1.5" />
            </div>
            <div>
              <Label className="text-zinc-300">Note <span className="text-zinc-600">(opzionale)</span></Label>
              <Textarea name="notes" rows={3} className="bg-zinc-950 border-zinc-800 mt-1.5" />
            </div>
            <DialogFooter className="pt-2">
              <Button type="submit" disabled={submitting} className="bg-yellow-400 text-zinc-950 hover:bg-yellow-300 font-display font-bold w-full">
                {submitting ? "Invio…" : "Invia richiesta di prenotazione"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
